/**
 * AdminQuotesPage ✦ iter226 — /admin/quotes
 *
 * The archive view. Lists every quote across all bookings,
 * filterable by status, searchable by customer/number. Clicking
 * a row opens the shared QuoteViewerModal so the workflow is
 * identical whether the admin lands here or comes from a
 * booking's "Generate Quote ✦" button.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import QuoteViewerModal from '../components/QuoteViewerModal';
import QuoteDashboardCards from '../components/QuoteDashboardCards';
import ExpiringQuotesStrip from '../components/ExpiringQuotesStrip';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const GOLD = '#D4AF5E';
const GOLD_FAINT = 'rgba(212,170,82,0.22)';
const IVORY = '#FBEED0';
const IVORY_DIM = 'rgba(251,238,208,0.62)';
const IVORY_GHOST = 'rgba(251,238,208,0.42)';

const STATUSES = [
  { key: 'all',      label: 'All' },
  { key: 'draft',    label: 'Draft' },
  { key: 'sent',     label: 'Sent' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'declined', label: 'Declined' },
  { key: 'expired',  label: 'Expired' },
];

const STATUS_STYLE = {
  draft:    { tint: 'rgba(226,199,123,0.30)', text: '#F4E2A8' },
  sent:     { tint: 'rgba(212,170,82,0.30)',  text: '#F0DCA8' },
  accepted: { tint: 'rgba(125,181,140,0.42)', text: '#CFEFC6' },
  declined: { tint: 'rgba(184,111,111,0.32)', text: '#E6B5B5' },
  expired:  { tint: 'rgba(140,110,110,0.30)', text: '#D1A8A8' },
};

const prettyDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const AdminQuotesPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    if (auth?.user === null) navigate('/login?next=/admin/quotes');
  }, [auth?.user, navigate]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/quotes`);
      setQuotes(r.data.quotes || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      if (e?.response?.status !== 401) toast.error('Could not load quotes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    let xs = quotes;
    if (filter !== 'all') xs = xs.filter(q => q.status === filter);
    if (search.trim()) {
      const t = search.trim().toLowerCase();
      xs = xs.filter(q =>
        (q.customer_name || '').toLowerCase().includes(t) ||
        (q.customer_email || '').toLowerCase().includes(t) ||
        (q.quote_number || '').toLowerCase().includes(t) ||
        (q.venue || '').toLowerCase().includes(t),
      );
    }
    return xs;
  }, [quotes, filter, search]);

  return (
    <div data-testid="admin-quotes-page" style={pageStyle}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
          <div>
            <div style={eyebrowStyle}>✦ Quotes &amp; Invoices</div>
            <h1 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(26px, 4vw, 36px)',
              margin: 0, letterSpacing: '-0.015em' }}>
              The quote archive
            </h1>
            <p style={{ fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM,
              fontSize: 13.5, margin: '8px 0 0', maxWidth: 640, lineHeight: 1.55 }}>
              Every quote you&apos;ve ever raised. Click a row to preview, download, email or mark its deposit received.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={() => navigate('/admin/bookings')}
              data-testid="quotes-bookings-link"
              style={ghostPillStyle}>← Bookings</button>
            <button type="button" onClick={() => navigate('/admin')}
              data-testid="quotes-admin-link"
              style={ghostPillStyle}>Admin home</button>
          </div>
        </header>

        {/* iter227 — conversion + forecast + expiring strip */}
        <QuoteDashboardCards refreshKey={quotes.length} />
        <ExpiringQuotesStrip onView={(qid) => setViewing(qid)} />

        {/* iter227 — follow-up queue (sent + forgotten) */}
        <FollowUpQueue onView={(qid) => setViewing(qid)} refreshKey={quotes.length} />

        {/* Filter + search */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18, alignItems: 'center' }}>
          {STATUSES.map(s => (
            <button type="button" key={s.key}
              onClick={() => setFilter(s.key)}
              data-testid={`quotes-filter-${s.key}`}
              style={{
                padding: '6px 14px', borderRadius: 999,
                border: `1px solid ${filter === s.key ? GOLD : 'rgba(255,255,255,0.14)'}`,
                background: filter === s.key ? 'rgba(212,170,82,0.18)' : 'rgba(255,255,255,0.04)',
                color: filter === s.key ? GOLD : IVORY_DIM,
                fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5, cursor: 'pointer',
              }}>
              {s.label} · {s.key === 'all' ? quotes.length : (counts[s.key] || 0)}
            </button>
          ))}
          <input
            placeholder="Search name · email · quote number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="quotes-search"
            style={{
              marginLeft: 'auto', minWidth: 220,
              background: 'rgba(0,0,0,0.32)', border: '1px solid rgba(255,255,255,0.14)',
              borderRadius: 999, padding: '6px 14px',
              color: IVORY, fontFamily: SERIF, fontSize: 13, fontStyle: 'italic',
              outline: 'none',
            }} />
        </div>

        {loading ? (
          <p style={{ fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM, textAlign: 'center', padding: '60px 0' }}>
            Loading quotes ✦
          </p>
        ) : visible.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px', fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM }}>
            No quotes yet. Generate one from <span style={{ color: GOLD }}>/admin/bookings</span> by clicking <span style={{ color: GOLD }}>Generate Quote ✦</span> on any booking.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {visible.map((q) => {
              const s = STATUS_STYLE[q.status] || STATUS_STYLE.draft;
              return (
                <div key={q.quote_id}
                  data-testid={`quote-row-${q.quote_id}`}
                  onClick={() => setViewing(q.quote_id)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(110px,140px) minmax(180px,1.6fr) minmax(120px,1fr) 110px 110px 100px',
                    alignItems: 'center', gap: 14, padding: '14px 18px',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12,
                    cursor: 'pointer', transition: 'border-color 200ms ease',
                  }}>
                  <div style={{ fontFamily: SERIF, fontSize: 13.5, color: IVORY, fontWeight: 500 }}>
                    {q.quote_number}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: SERIF, fontSize: 14.5, color: IVORY, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {q.customer_name || '(no name)'}
                    </div>
                    <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12, color: IVORY_DIM, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {q.customer_email}{q.venue ? ` · ${q.venue}` : ''}
                    </div>
                  </div>
                  <div style={{ fontFamily: SERIF, fontSize: 12.5, color: IVORY_DIM, fontStyle: 'italic' }}>
                    {prettyDate(q.event_date)}
                  </div>
                  <div style={{ fontFamily: SERIF, fontSize: 13.5, color: IVORY, textAlign: 'right' }}>
                    £{(q.subtotal_pounds || 0).toLocaleString('en-GB')}
                  </div>
                  <div style={{ fontFamily: SERIF, fontSize: 11.5, color: IVORY_GHOST, textAlign: 'right' }}>
                    Dep £{q.deposit_required_pounds}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{
                      padding: '4px 10px', borderRadius: 999,
                      background: s.tint, color: s.text,
                      border: `1px solid ${s.tint}`,
                      fontFamily: SERIF, fontSize: 11.5, fontStyle: 'italic',
                      letterSpacing: '0.04em',
                    }}>{q.status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {viewing && (
        <QuoteViewerModal
          quoteId={viewing}
          onClose={() => setViewing(null)}
          onChange={load}
        />
      )}
    </div>
  );
};

const pageStyle = {
  minHeight: '100vh', background: '#0F0B1A', color: IVORY, fontFamily: SERIF,
  padding: 'clamp(40px, 6vw, 70px) clamp(16px, 3vw, 40px) 80px',
};
const eyebrowStyle = {
  fontFamily: SERIF, fontStyle: 'italic',
  fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
  color: GOLD, marginBottom: 8,
};
const ghostPillStyle = {
  padding: '9px 18px', borderRadius: 999,
  background: 'transparent', border: `1px solid ${GOLD_FAINT}`,
  color: IVORY_DIM, fontFamily: SERIF, fontStyle: 'italic',
  fontSize: 13, cursor: 'pointer',
};

export default AdminQuotesPage;

// ─── iter227 · Follow-Up Queue ───────────────────────────────────
const FollowUpQueue = ({ onView, refreshKey }) => {
  const [queue, setQueue] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/quote-pipeline/follow-up-queue`)
      .then(r => setQueue(r.data?.queue || []))
      .catch(() => setQueue([]))
      .finally(() => setLoaded(true));
  }, [refreshKey]);

  if (!loaded || queue.length === 0) return null;

  return (
    <section data-testid="follow-up-queue" style={{
      marginBottom: 24, padding: 'clamp(18px, 2.5vw, 24px)',
      border: `1px solid ${GOLD_FAINT}`, borderRadius: 14,
      background: 'rgba(255,255,255,0.03)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'baseline', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
            color: GOLD, marginBottom: 4 }}>
            ✦ Follow-Up Queue
          </div>
          <div style={{ fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 13, color: IVORY_DIM, lineHeight: 1.45 }}>
            Quotes you&apos;ve sent but the customer hasn&apos;t decided on yet — forgotten first.
          </div>
        </div>
        <span style={{
          padding: '4px 14px', borderRadius: 999,
          background: 'rgba(212,170,82,0.18)', color: GOLD,
          fontFamily: SERIF, fontStyle: 'italic', fontSize: 12, letterSpacing: '0.04em',
        }}>{queue.length} awaiting decision</span>
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {queue.slice(0, 12).map((q) => (
          <div key={q.quote_id} data-testid={`follow-up-row-${q.quote_id}`}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(110px,140px) minmax(180px,1.6fr) minmax(110px,1fr) minmax(110px,1fr) auto',
              alignItems: 'center', gap: 12,
              padding: '12px 14px', borderRadius: 10,
              background: 'rgba(0,0,0,0.18)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}>
            <div style={{ fontFamily: SERIF, fontSize: 13.5, color: IVORY, fontWeight: 500 }}>
              {q.quote_number}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: SERIF, fontSize: 14, color: IVORY, fontWeight: 500,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {q.customer_name || '(unnamed)'}
              </div>
              <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 11.5, color: IVORY_DIM,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {q.customer_email}
              </div>
            </div>
            <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5, color: IVORY_DIM }}>
              <div>{q.days_since_sent ?? '—'}d since sent</div>
              <div style={{ color: IVORY_GHOST, fontSize: 11 }}>
                {q.last_contact_date
                  ? `Chased ${q.days_since_contact}d ago`
                  : 'Not chased yet'}
              </div>
            </div>
            <div style={{ fontFamily: SERIF, fontSize: 13, color: IVORY }}>
              £{(q.subtotal_pounds || 0).toLocaleString('en-GB')}
            </div>
            <button type="button" onClick={() => onView?.(q.quote_id)}
              data-testid={`follow-up-view-${q.quote_id}`}
              style={{
                padding: '6px 14px', borderRadius: 999,
                border: `1px solid ${GOLD}`,
                background: 'transparent', color: IVORY,
                fontFamily: SERIF, fontSize: 12.5, cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}>View Quote ✦</button>
          </div>
        ))}
      </div>
    </section>
  );
};

